import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { useSpotHoldings } from '@flipster/features/src/shared/spot-holdings/state';
import { useSymbols } from '@flipster/features/src/shared/symbol/use-symbols';
import { openPositionsBunja } from '@flipster/socket/r230522/position';
import { QuoteCurrencyListExceptUsdt } from '@flipster/utils/constants/currency';

import { MarketCarouselType } from './constants';

export function useSortedTopMarket(productType?: 'perpetual' | 'spot') {
  const { isLoggedIn } = useAuth();
  const { symbols } = useSymbols(productType);
  const { topOpenPositionsAtom } = useBunja(openPositionsBunja);
  const topOpenPositions = useAtomValue(topOpenPositionsAtom);
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const spotInstruments = Object.entries(instrumentsKeyBySymbol || {}).filter(([, value]) => value.isSpot);
  const { holdings: holdingList } = useSpotHoldings();

  const holdings = holdingList.reduce((acc, [key]) => {
    const spotItem = spotInstruments.find(([, spotValue]) => spotValue.baseCurrency === key);
    if (!spotItem) return acc;
    const [symbol] = spotItem;
    return [...acc, symbol];
  }, [] as string[]);

  const quoteCurrencyPairs = QuoteCurrencyListExceptUsdt.reduce(
    (acc, currency) => {
      acc[currency] =
        productType === 'perpetual'
          ? symbols?.marketCap.filter((symbol) => symbol.endsWith(`${currency}.PERP`)).slice(0, 3)
          : [];
      return acc;
    },
    {} as Record<(typeof QuoteCurrencyListExceptUsdt)[number], string[] | undefined>,
  );

  const sortedTopMarkets: Record<MarketCarouselType, string[] | undefined> = {
    yourOpenPositions: productType === 'perpetual' && isLoggedIn ? topOpenPositions : [],
    yourHoldings: productType === 'spot' && isLoggedIn ? holdings.slice(0, 3) : [],
    // hotTradings는 3개 이상인 경우에만 표시하도록 함
    hotTradings:
      productType === 'perpetual' && (symbols?.hotTradings?.length || 0) >= 3 ? symbols?.hotTradings.slice(0, 3) : [],
    newListings: symbols?.newListings.slice(0, 3),
    priceChange24h: symbols?.priceChange24h.slice(0, 3),
    volume: symbols?.volume.slice(0, 3),
    absFundingRates: productType === 'perpetual' ? symbols?.absFundingRates.slice(0, 3) : [],
    yourFrequentTrades: symbols?.yourFrequentTrades?.slice(0, 3),
    ...quoteCurrencyPairs,
  };

  return { sortedTopMarkets };
}
